import { CompensationProfileModel, EmployeeModel, EmployeeStatusModel, IcdOfficeEmployeeModel, IcdOfficeListResponse, JobTitleListModel, PayFrequency, TerminationReasonModel, WagePolicyModel, BasisOfPayModel, StateProvinceModel } from "@herbjoy/apex-api-models";
import { IFormController, IQService } from "angular";
import { departmentList } from "../../../../core/enum/department";
import { DivisionService, EmployeeService, LocaleService, OnboardingApplicationService } from "../../../../core/services";
import { UtilityService } from "../../../../core/services/utility.service";

export interface RoleSelection {
    RoleID: number;
    IsInRole: boolean;
}

export interface ModuleSelection {
    ModuleID: number;
    IsModuleAdmin: boolean;
}

export type EmploymentRecordInputModel = Omit<Omit<IcdOfficeEmployeeModel, "AdministeredModules">, "Roles"> & {
    EmployeeStatus: EmployeeStatusModel;
    TerminationReason: TerminationReasonModel;
    Roles: RoleSelection[];
    AdministeredModules: ModuleSelection[];
    RoleList: RoleSelection[];
    PayFrequency: PayFrequency;
    CanDelete: boolean;
    expanded: boolean;
    changing: boolean;
    icdOfficeEmployeeForm: IFormController;
}

export interface EmploymentRecordEditContext {
    loaded: boolean;
    employee: EmployeeModel;
    icdOffices: IcdOfficeListResponse[];
    employmentStatuses: EmployeeStatusModel[];
    terminationReasons: TerminationReasonModel[];
    payFrequencies: PayFrequency[];
    jobTitles: JobTitleListModel[];
    compensationProfiles: CompensationProfileModel[];
    wagePolicies: WagePolicyModel[];
    basisOfPay: BasisOfPayModel[];
    stateProvinces: StateProvinceModel[];

    changeEmployeeStatus(office: EmploymentRecordHybridModel);
    changeTerminationReason(office: EmploymentRecordHybridModel);
    /**
     * Handle changes to the employment record's selected office.
     * @param {any} office The employment record that was changed.
     */
    changeEmploymentRecordIcdOffice(office: EmploymentRecordHybridModel);
    changeRole(office: EmploymentRecordHybridModel);
    changePayFrequency(office: EmploymentRecordHybridModel);
    changeJobTitle(office: EmploymentRecordHybridModel);
    changeBasisOfPay(office: EmploymentRecordHybridModel);
    changeCompensationProfile(office: EmploymentRecordHybridModel);
    changeDivisions(office: EmploymentRecordHybridModel);
    anyRolesAssigned(office: EmploymentRecordHybridModel): boolean;
    validateMinimumWage(office: EmploymentRecordHybridModel);
    validateMinimumWageV2(office: EmploymentRecordHybridModel, stateID);
}

export type EmploymentRecordHybridModel = IcdOfficeEmployeeModel | EmploymentRecordInputModel;

type EmploymentRecordConstructorParameters = [
    EmployeeModel,
    IcdOfficeListResponse[],
    EmployeeStatusModel[],
    TerminationReasonModel[],
    PayFrequency[],
    JobTitleListModel[],
    CompensationProfileModel[],
    WagePolicyModel[],
    BasisOfPayModel[],
    StateProvinceModel[]
];

/**
 * A factory that can be used to create a context for hydrating employment
 * record values.
 */
export function EmploymentRecordEditContextFactory($q: IQService, divisionService: DivisionService,
    employeeService: EmployeeService, localeService: LocaleService,
    onboardingApplicationService: OnboardingApplicationService, utilityService: UtilityService) {
    'ngInject'

    function createContext(...options: EmploymentRecordConstructorParameters): EmploymentRecordEditContext {
        return new EmploymentRecordEditContextImpl(...options);
    }

    class EmploymentRecordEditContextImpl implements EmploymentRecordEditContext {
        loaded: boolean;
        employee: EmployeeModel;
        icdOffices: IcdOfficeListResponse[];
        employmentStatuses: EmployeeStatusModel[];
        terminationReasons: TerminationReasonModel[];
        payFrequencies: PayFrequency[];
        jobTitles: JobTitleListModel[];
        compensationProfiles: CompensationProfileModel[];
        wagePolicies: WagePolicyModel[];
        basisOfPay: BasisOfPayModel[];
        stateProvinces: StateProvinceModel[];

        constructor(employee, icdOffices, employmentStatuses, terminationReasons,
            payFrequencies, jobTitles, compensationProfiles, wagePolicies, basisOfPay, stateProvinces) {

            this.loaded = false;
            this.employee = employee;
            this.icdOffices = icdOffices;
            this.employmentStatuses = employmentStatuses;
            this.terminationReasons = terminationReasons;
            this.payFrequencies = payFrequencies;
            this.jobTitles = jobTitles;
            this.compensationProfiles = compensationProfiles;
            this.wagePolicies = wagePolicies;
            this.basisOfPay = basisOfPay;
            this.stateProvinces = stateProvinces;
            // Hydrate employment record based on contextual object lists by ID.
            const loading = [];
            let i = 0;
            for (const office of employee.IcdOffices) {
                loading.push(this._loadEmployeeIcdOffice(office)
                    .then(() => {
                        this.changeEmployeeStatus(office);
                        this.changeDivisions(office);
                        // Assign drop-down objects to the office based on the ID of the office.
                        this._assignJobTitleByID(office);
                        this._assignBasisOfPayByID(office);
                        this._assignPayFrequencyByID(office);
                        this._assignCompensationProfileByID(office);
                        this.changeTerminationReason(office);
                        this._updateOfficeModuleAdminsGrantedByRoles(office,
                            this.employee.GlobalRoles);
                    }));
                office.CanDelete = false;
                office.expanded = i === 0;
                i++;
            }
            utilityService.showLoadingUntilResolved($q.all(loading)
                .catch(ex => console.error(ex))
                .then(() => this.loaded = true));
        }

        changeEmployeeStatus(office: EmploymentRecordInputModel) {
            office.EmployeeStatus = this.employmentStatuses.filter(function (status) {
                return Number(status.EmployeeStatusID) === Number(office.EmployeeStatusID);
            })[0];
        }

        changeTerminationReason(office: EmploymentRecordInputModel) {
            office.TerminationReason = this.terminationReasons.find(
                reason => reason.TerminationReasonID === office.TerminationReasonID);
        }

        /**
         * Handle changes to the employment record's selected office.
         * @param {any} office The employment record that was changed.
         */
        changeEmploymentRecordIcdOffice(_office: EmploymentRecordHybridModel) {
            const office: EmploymentRecordInputModel = _office as EmploymentRecordInputModel;
            const target = _office as IcdOfficeEmployeeModel;
            if (office.changing) {
                return;
            }
            // Changing the office can completely change which values are possible for
            // selection in other fields depending on the user's roles
            office.changing = true;

            // First, reduce the employee's roles and modules to an object so they can be loaded by lookup.
            target.AdministeredModules = office.AdministeredModules.reduce(function (res, module) {
                res[module.ModuleID] = module.IsModuleAdmin;
                return res;
            }, {});
            target.Divisions = office.Divisions.map(function (division: any) {
                return division.Id;
            });

            // This will reassign the modules and roles back into a list of objects.
            this._loadEmployeeIcdOffice(office)
                .then(() => {
                    // Trigger an update to automatically granted module admins.
                    this._updateOfficeModuleAdminsGrantedByRoles(office, this.employee.GlobalRoles);
                    // This function will update validation properties on the office, but does
                    // not factor into overall validation.
                    return this.validateMinimumWage(office)
                })
                .then(() => {
                    // Changes to the office can also trigger changes to the divisions, so fire the handler.
                    this.changeDivisions(office);
                })
                .catch(function () {

                })
                .then(function () {
                    office.changing = false;
                });

        }

        changeRole(_office: EmploymentRecordHybridModel) {
            const office: EmploymentRecordInputModel = _office as EmploymentRecordInputModel;
            const target = _office as IcdOfficeEmployeeModel;
            target.Roles = office.RoleList.reduce(function (res, role) {
                res[role.RoleID] = role.IsInRole;
                return res;
            }, {});
            //TODO: Replace with new permissions systems.
            this._updateModuleAdminsGrantedByRoles(this.employee.IcdOffices, this.employee.GlobalRoles);
        }

        changePayFrequency(office: EmploymentRecordInputModel) {
            office.icdOfficeEmployeeForm.$setDirty();
            office.PayFrequencyID = office.PayFrequency ? office.PayFrequency.PayFrequencyID : null;
        }

        changeJobTitle(office) {
            office.icdOfficeEmployeeForm.$setDirty();
            office.JobTitleID = office.JobTitle ? office.JobTitle.Id : null;
        }

        changeBasisOfPay(office) {
            office.icdOfficeEmployeeForm.$setDirty();
            office.BasisOfPayID = office.BasisOfPay ? office.BasisOfPay.BasisOfPayId : null;
        }

        changeCompensationProfile(office) {
            office.icdOfficeEmployeeForm.$setDirty();
            office.CompensationProfileID = office.CompensationProfile ? office.CompensationProfile.Id : null;
            this.validateMinimumWage(office);
        }

        changeDivisions(office) {
            office.DivisionIDs = office.Divisions.map(function (division) {
                return division.Id;
            });
            office.ShowCommissionThreshold = office.Divisions.reduce(function (any, division) {
                return any || division.UseCommissionThreshold;
            }, false);
        }

        anyRolesAssigned(office) {
            return office.RoleList.reduce(function (any, role) {
                return any || role.IsInRole;
            }, false);
        }

        validateMinimumWageV2(office, stateID) {
            office.icdOfficeEmployeeForm.$setDirty();
            if (stateID === 0 && !office.IcdOffice) {
                // Nothing to validate since no office is selected.
                return;
            }

            const icdOffice = office.IcdOffice;


            if (stateID !== 0) {
                icdOffice.WagePolicy = this.wagePolicies.find(wagePolicy =>
                    wagePolicy.StateProvinceID === stateID);
            } else {
                icdOffice.WagePolicy = this.wagePolicies.find(wagePolicy =>
                    wagePolicy.StateName === icdOffice.OfficeState);
            }


            // Display the warning outside of ngForm.$error, since it
            // shouldn't cause the model to fail validation.
            const belowMinimumWage = icdOffice.WagePolicy && office.PayRate < icdOffice.WagePolicy.MinimumWage &&
                (office.CompensationProfile == null || (office.CompensationProfile != null && office.CompensationProfile.Id === departmentList.dept400));
            office.icdOfficeEmployeeForm['PayRate_' + office.$id].$setValidity("belowMinimumWage", !belowMinimumWage);
        }

        validateMinimumWage(office) {
            if (!office.IcdOffice) {
                // Nothing to validate since no office is selected.
                return;
            }

            const icdOffice = office.IcdOffice;

            if (!icdOffice.WagePolicy) {
                icdOffice.WagePolicy = this.wagePolicies.find(wagePolicy =>
                    wagePolicy.StateName === icdOffice.OfficeState);
            }

            // Display the warning outside of ngForm.$error, since it
            // shouldn't cause the model to fail validation.
            const belowMinimumWage = icdOffice.WagePolicy && office.PayRate < icdOffice.WagePolicy.MinimumWage &&
                office.CompensationProfile != null && office.CompensationProfile.Id === departmentList.dept400;
            office.icdOfficeEmployeeForm['PayRate_' + office.$id].$setValidity("belowMinimumWage", !belowMinimumWage);
        }

        _assignPayFrequencyByID(office) {
            office.PayFrequency = this.payFrequencies.filter(function (payFrequency) {
                return payFrequency.PayFrequencyID === office.PayFrequencyID;
            })[0];
        }

        _assignJobTitleByID(office) {
            office.JobTitle = this.jobTitles.filter(function (jobTitle) {
                return jobTitle.Id === office.JobTitleID;
            })[0];
        }

        _assignBasisOfPayByID(office) {
            office.BasisOfPay = this.basisOfPay.filter(function (basisOfPay) {
                return basisOfPay.BasisOfPayId === office.BasisOfPayID;
            })[0];
        }
        _assignCompensationProfileByID(office) {
            office.CompensationProfile = this.compensationProfiles.filter(function (profile) {
                return profile.Id === office.CompensationProfileID;
            })[0];
        }

        _updateModuleAdminsGrantedByRoles(offices, globalRoles) {
            offices.forEach(office => this._updateOfficeModuleAdminsGrantedByRoles(office, globalRoles));
        }

        _updateOfficeModuleAdminsGrantedByRoles(office, globalRoles) {
            office.AdministeredModules.forEach(module => {
                module.IsGrantedByRole = this._isModuleAdminGrantedByRoles(module, globalRoles)
                    || this._isModuleAdminGrantedByRoles(module, office.RoleList);
            });
        }

        _isModuleAdminGrantedByRoles(module, roles) {
            return roles.reduce(function (any, role) {
                return any || role.IsInRole && role.AdministeredModules.reduce(function (any, roleModule) {
                    return any || roleModule === module.ModuleID;
                }, false);
            }, false);
        }

        /**
         * Load all necessary data from asynchronous resources into the given IcdOfficeEmployee
         * @param {any} office The IcdOfficeEmployee that represents an employee on the roster.
         * @returns {any} The resolved employment record for the office employee.
         */
        _loadEmployeeIcdOffice(office) {
            const tasks = [];
            // Load the IcdOffice data from the currently set ID.
            office.IcdOfficeID = office.IcdOfficeID || null; // Set null if the id was 0 by default (since the api model is non-nullable)
            office.IcdOffice = this.icdOffices.filter(function (icdOffice) {
                return icdOffice.Id === office.IcdOfficeID;
            })[0];
            const loadIcdOfficeData = $q.resolve(office.IcdOffice);
            tasks.push(loadIcdOfficeData);

            // Get the roles list into the office.
            const loadIcdOfficeRolesData = loadIcdOfficeData.then(p => this._loadIcdOfficeRoles(p));
            tasks.push(loadIcdOfficeRolesData);

            // Map the roles selected by the employee to the collection of roles assignable by the current user.
            const mapEmployeeIcdOfficeRolesData = loadIcdOfficeRolesData.then(
                roles => mapEmployeeIcdOfficeRoles(office, roles));
            tasks.push(mapEmployeeIcdOfficeRolesData);

            // Get the module list into the office.
            const loadIcdOfficeModulesData = loadIcdOfficeData.then(p => this._loadIcdOfficeModules(p));
            tasks.push(loadIcdOfficeModulesData);

            // Map the modules selected by the empoyee to the collection of modules assignable by the current user.
            const mapEmployeeIcdOfficeModulesData = loadIcdOfficeModulesData.then(
                modules => mapEmployeeIcdOfficeModules(office, modules));
            tasks.push(mapEmployeeIcdOfficeModulesData);

            // Get the division list into the office.
            const loadIcdOfficeDivisionsData = loadIcdOfficeData.then(p => this._loadIcdOfficeDivisions(p));
            tasks.push(loadIcdOfficeDivisionsData);

            const mapEmployeeIcdOfficeDivisionsData = loadIcdOfficeDivisionsData.then(
                divisions => mapEmployeeIcdOfficeDivisions(office, divisions));
            tasks.push(mapEmployeeIcdOfficeDivisionsData);

            let loadOnboardingApplication = $q.resolve();
            if (office.OnboardingApplicationID) {
                loadOnboardingApplication = loadOnboardingApplication
                    .then(() => onboardingApplicationService
                        .retrieveOnboardingApplication(office.OnboardingApplicationID))
                    .then(function (application) {
                        office.OnboardingApplication = application;
                    });
            }
            tasks.push(loadOnboardingApplication);


            return $q.all(tasks)
                .then(function (v) {
                    return office;
                });
        }

        _loadIcdOfficeRoles(icdOffice) {
            if (!icdOffice || !icdOffice.Id) {
                return $q.when([]);
            }
            return icdOffice.RoleList
                ? $q.when(icdOffice.RoleList)
                : employeeService.retrieveEmployeeAssignableRoles(icdOffice.Id).then(function (roles) {
                    icdOffice.RoleList = roles.filter(function (role) {
                        return !role.GlobalRole;
                    });
                    return icdOffice.RoleList;
                })
                    .then(p => this._loadRoleDescriptions(p));
        }

        _loadIcdOfficeModules(icdOffice) {
            if (!icdOffice || !icdOffice.Id) {
                return $q.when([]);
            }
            return employeeService.retrieveAdministeredModules(icdOffice.Id).then(function (modules) {
                icdOffice.Modules = modules;
                return icdOffice.Modules;
            });

        }

        /**
         * Load any dynamic description data associated with the list of roles.
         * @param {Promise<Role>} roles The list of roles to load.
         * @returns {Promise<Role[]>} The resolved role with localized descriptions loaded.
         */
        _loadRoleDescriptions(roles) {
            return $q.when(roles)
                .then(function (roles) {
                    return $q.all(roles.map(function (role) {
                        if (!role.Description || role.Description[0] !== "|") {
                            return $q.when(role);
                        }
                        // Translate the description using the locale service.
                        return localeService.retrieveCurrentLocaleTranslations()
                            .then(function (translations) {
                                role.Description = translations[role.Description.slice(1)];
                                return role;
                            });
                    }));
                });
        }

        _loadIcdOfficeDivisions(icdOffice) {
            if (!icdOffice || !icdOffice.Id) {
                return $q.when([]);
            }

            // Load the divisions into the office. Only need to load the division list for each office once.
            return icdOffice.Divisions
                ? $q.when(icdOffice.Divisions)
                : divisionService.retrieveIcdOfficeDivisions(icdOffice.Id).then(function (divisions) {
                    icdOffice.Divisions = divisions;
                    return divisions;
                });
        }
    }
    return createContext;
}

function mapEmployeeIcdOfficeRoles(office, roles) {
    office.RoleList = roles.map(function (role) {
        return mapEmployeeRole(role, !!office.Roles[role.Id]);
    });
    return office.RoleList;
}

function mapEmployeeRole(role, isInRole) {
    return {
        RoleID: role.Id,
        Name: role.Name,
        Description: role.Description,
        IsInRole: isInRole,
        AdministeredModules: role.AdministeredModules
    };
}

function mapEmployeeIcdOfficeModules(office, modules) {
    office.AdministeredModules = modules.map(function (module) {
        return {
            ModuleID: module.Id,
            Name: module.Name,
            Description: module.Description,
            IsModuleAdmin: !!office.AdministeredModules[module.Id]
        };
    });
    return office.AdministeredModules;
}

/**
 * Map the office employee's list of divisions from a list of division IDs to a list
 * of division objects, using the master division list as a lookup.
 * @param {any} office An IcdOfficeEmployee that contains the list of divisions the
 * employee is assigned at the office.
 * @param {any} divisions A lookup list of Division models.
 * @returns {any} The resolved divisions.
 */
function mapEmployeeIcdOfficeDivisions(office, divisions) {
    office.Divisions = divisions.filter(function (division) {
        // Replace division IDs with the actual divisions.
        // This is needed to preload with the kendo multi select.
        return office.Divisions.filter(function (officeDivision) {
            return officeDivision && officeDivision.Id === division.Id || officeDivision === division.Id;
        }).length > 0;
    });
    if (office.Divisions.length === 0 && divisions.length === 1 && !office.EmployeeID) {
        // Set at least one division as a default if a new office.
        office.Divisions = [divisions[0]];
    }
    return office.Divisions;
}
